import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { AppLayout } from "./layouts/AppLayout";
import Dashboard from "./pages/Dashboard";
import Questions from "./pages/Questions";
import NewQuestion from "./pages/NewQuestion";
import QuestionAudit from "./pages/QuestionAudit";
import ImportQuestions from "./pages/ImportQuestions";
import StartTest from "./pages/StartTest";
import TestRunner from "./pages/TestRunner";
import TestResult from "./pages/TestResult";
import History from "./pages/History";
import WritingErrors from "./pages/WritingErrors";
import Vocabulary from "./pages/Vocabulary";
import Flashcards from "./pages/Flashcards";
import FlashcardStudy from "./pages/FlashcardStudy";
import Settings from "./pages/Settings";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import LandingPage from "./pages/LandingPage";
import Pricing from "./pages/Pricing";
import NotFound from "./pages/NotFound";
import AdminDashboard from "./pages/AdminDashboard";
import AdminDebug from "./pages/AdminDebug";
import Upgrade from "./pages/Upgrade";
import GrammarTrail from "./pages/GrammarTrail";
import GrammarLesson from "./pages/GrammarLesson";
import EoVocabPlayer from "./pages/EoVocabPlayer";
import AdminGrammar from "./pages/AdminGrammar";
import AdminReading from "./pages/AdminReading";
import ReadingList from "./pages/ReadingList";
import ReadingArticle from "./pages/ReadingArticle";
import AdminBanqueExercices from "./pages/AdminBanqueExercices";
import BanqueExercicesList from "./pages/BanqueExercicesList";
import ExercisePlayer from "./pages/ExercisePlayer";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminRoute } from "./components/auth/AdminRoute";
import AppErrorBoundary from "./components/AppErrorBoundary";

const router = createBrowserRouter([
  {
    path: "/",
    element: <LandingPage />,
    errorElement: <AppErrorBoundary />
  },
  {
    path: "/pricing",
    element: <Pricing />,
    errorElement: <AppErrorBoundary />
  },
  {
    path: "/auth",
    element: <Auth />,
    errorElement: <AppErrorBoundary />
  },
  {
    element: <AppLayout />,
    errorElement: <AppErrorBoundary />,
    children: [
      { path: "/onboarding", element: <ProtectedRoute><Onboarding /></ProtectedRoute> },
      { path: "/dashboard", element: <ProtectedRoute><Dashboard /></ProtectedRoute> },
      
      // Admin Routes (with aliases)
      { path: "/admin/debug", element: <ProtectedRoute><AdminDebug /></ProtectedRoute> },
      { path: "/admin", element: <AdminRoute><AdminDashboard /></AdminRoute> },
      { path: "/questions", element: <AdminRoute><Questions /></AdminRoute> },
      { path: "/question-bank", element: <AdminRoute><Questions /></AdminRoute> },
      { path: "/questions/new", element: <AdminRoute><NewQuestion /></AdminRoute> },
      { path: "/new-question", element: <AdminRoute><NewQuestion /></AdminRoute> },
      { path: "/nova-questao", element: <AdminRoute><NewQuestion /></AdminRoute> },
      { path: "/questions/:id/edit", element: <AdminRoute><NewQuestion /></AdminRoute> },
      { path: "/questions/import", element: <AdminRoute><ImportQuestions /></AdminRoute> },
      { path: "/import-questions", element: <AdminRoute><ImportQuestions /></AdminRoute> },
      { path: "/importar-questoes", element: <AdminRoute><ImportQuestions /></AdminRoute> },
      { path: "/questions/audit", element: <AdminRoute><QuestionAudit /></AdminRoute> },
      { path: "/admin/grammar", element: <AdminRoute><AdminGrammar /></AdminRoute> },
      { path: "/admin/reading", element: <AdminRoute><AdminReading /></AdminRoute> },
      { path: "/admin/exercises", element: <AdminRoute><AdminBanqueExercices /></AdminRoute> },

      // Protected Study/Test Routes
      { path: "/start-test", element: <ProtectedRoute><StartTest /></ProtectedRoute> },
      { path: "/grammar", element: <ProtectedRoute><GrammarTrail /></ProtectedRoute> },
      { path: "/grammar/eo/:themeId", element: <ProtectedRoute><EoVocabPlayer /></ProtectedRoute> },
      { path: "/grammar/:slug", element: <ProtectedRoute><GrammarLesson /></ProtectedRoute> },
      { path: "/reading", element: <ProtectedRoute><ReadingList /></ProtectedRoute> },
      { path: "/reading/:slug", element: <ProtectedRoute><ReadingArticle /></ProtectedRoute> },
      { path: "/exercises", element: <ProtectedRoute><BanqueExercicesList /></ProtectedRoute> },
      { path: "/exercises/:id", element: <ProtectedRoute><ExercisePlayer /></ProtectedRoute> },
      { path: "/test/runner", element: <ProtectedRoute><TestRunner /></ProtectedRoute> },
      { path: "/test/result", element: <ProtectedRoute><TestResult /></ProtectedRoute> },
      { path: "/history", element: <ProtectedRoute><History /></ProtectedRoute> },
      { path: "/upgrade", element: <ProtectedRoute><Upgrade /></ProtectedRoute> },
      { path: "/premium", element: <ProtectedRoute><Upgrade /></ProtectedRoute> },
      { path: "/writing-errors", element: <ProtectedRoute><WritingErrors /></ProtectedRoute> },
      { path: "/vocabulary", element: <ProtectedRoute><Vocabulary /></ProtectedRoute> },
      { path: "/flashcards", element: <ProtectedRoute><Flashcards /></ProtectedRoute> },
      { path: "/flashcards/study", element: <ProtectedRoute><FlashcardStudy /></ProtectedRoute> },
      { path: "/settings", element: <ProtectedRoute><Settings /></ProtectedRoute> },

      // Fallback 404 Route
      { path: "*", element: <NotFound /> }
    ]
  }
]);

function App() {

  // SW Unregister for debug
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        console.log("[SW_UNREGISTER_FOR_NAV_DEBUG]", registration);
        registration.unregister();
      });
    });
  }

  // GLOBAL CLICK LISTENER FOR DIAGNOSIS
  if (typeof window !== "undefined" && !(window as any).__globalClickListenerAdded) {
    (window as any).__globalClickListenerAdded = true;
    document.addEventListener("click", e => {
      console.log("[GLOBAL_CLICK_TARGET]", e.target);
    }, true);
  }

  return (
    <RouterProvider router={router} />
  );
}

export default App;
